import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Clock, BookOpen } from "lucide-react";
import { Reveal, SectionHeading } from "../components/primitives";
import { PageHero, CTASection } from "../components/sections";
import { POSTS, BLOG_CATEGORIES } from "../data/site";
import { setPageSeo } from "../lib/seo";

export const Blog = () => (
  <>
    <PageHero
      eyebrow="The DWC Blog"
      lines={["Driving Psychology,", "Confidence & Road Craft."]}
      text="Practical articles on driving psychology, confidence and road skills — written for real Indian road conditions."
    />
    <section className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex flex-wrap gap-2" data-testid="blog-categories">
          {BLOG_CATEGORIES.map((c) => (
            <span key={c} className="rounded-full border border-dwc-green/20 bg-dwc-soft px-4 py-1.5 text-xs font-bold text-dwc-green">{c}</span>
          ))}
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {POSTS.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 3) * 0.08} className="h-full">
              <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${i}`} className="group flex h-full flex-col rounded-2xl border border-gray-100 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgb(23,107,58,0.10)]">
                <div className="flex items-center gap-3">
                  <span className="rounded-full bg-dwc-soft px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-dwc-green">{p.category}</span>
                </div>
                <h2 className="mt-4 text-xl font-extrabold tracking-tight text-dwc-charcoal transition-colors group-hover:text-dwc-green">{p.title}</h2>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-dwc-charcoal/65">{p.excerpt}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-extrabold uppercase tracking-wide text-dwc-green">
                  Read Article
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-12 rounded-2xl border border-dwc-green/15 bg-dwc-soft p-6 text-sm text-dwc-charcoal/65" data-testid="blog-note">
            New articles are added regularly across driving psychology, confidence, women & driving, senior driving, defensive driving and road safety. Facing a specific challenge? Start from the <Link to="/driving-problems" className="font-extrabold text-dwc-green underline underline-offset-4">Driving Problems</Link> page.
          </p>
        </Reveal>
      </div>
    </section>
    <CTASection title="Reading Helps. Training Transforms." sub="Book an appointment and turn understanding into real confidence on the road." chapter="02" />
  </>
);

export const BlogPost = () => {
  const { slug } = useParams();
  const post = POSTS.find((p) => p.slug === slug);
  useEffect(() => {
    if (post) setPageSeo(`${post.title} | Drive With Confidence — Mumbai, Surat & Ahmedabad`, post.excerpt);
  }, [post]);
  if (!post) return <Navigate to="/blog" replace />;
  return (
    <>
      <section className="bg-dwc-soft pb-14 pt-32 md:pt-40">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <Reveal>
            <div className="flex items-center gap-3">
              <span className="rounded-full bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-dwc-green">{post.category}</span>
              <span className="inline-flex items-center gap-1.5 text-xs text-dwc-charcoal/50"><Clock className="h-3.5 w-3.5" aria-hidden="true" />4 min read</span>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-5 text-3xl font-extrabold tracking-tight text-dwc-charcoal sm:text-4xl lg:text-5xl leading-[1.08] text-balance">{post.title}</h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-5 text-base leading-relaxed text-dwc-charcoal/70 md:text-lg">{post.excerpt}</p>
          </Reveal>
        </div>
      </section>
      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          {post.content.map((para, i) => (
            <Reveal key={i} delay={0.05}>
              <p className="mb-6 text-base leading-[1.85] text-dwc-charcoal/80">{para}</p>
            </Reveal>
          ))}
          {post.related && post.related.length > 0 && (
            <Reveal delay={0.05}>
              <div className="mt-8 rounded-2xl border border-dwc-green/15 bg-dwc-soft p-6" data-testid="blog-related-links">
                <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-dwc-green">Related Reading & Next Steps</p>
                <ul className="mt-3 space-y-2">
                  {post.related.map((r) => (
                    <li key={r.to}>
                      <Link to={r.to} className="group inline-flex items-center gap-1.5 text-sm font-bold text-dwc-charcoal transition-colors hover:text-dwc-green">
                        {r.label}
                        <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          )}
          <Reveal delay={0.1}>
            <div className="mt-10 rounded-2xl bg-dwc-green p-8 text-white grain relative overflow-hidden">
              <BookOpen className="h-6 w-6 text-emerald-200" aria-hidden="true" />
              <p className="mt-3 text-lg font-extrabold tracking-tight">Ready to work on this properly?</p>
              <p className="mt-1 text-sm text-white/80">Book an appointment with DWC — practical training plus driving psychology, in Mumbai, Surat & Ahmedabad.</p>
              <Link to="/contact" data-testid="blog-post-cta" className="group mt-5 inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3 text-sm font-bold uppercase tracking-wide text-dwc-green">
                Book an Appointment
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};
